import { useMemo } from 'react';
import { MultiSelectionToggleGroup } from 'src/components/common/MultiSelect/Toggle/MultiSelectionToggleGroup';
import { MetricsDisplayPlaceholder } from 'src/components/Metrics';
import { PurchaseTileActionDropdown } from 'src/components/Purchases/PurchaseTileActionDropdown';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { ContentId } from 'src/utils/constants/contentId';
import { formatCurrency } from 'src/utils/numberFormatter';
import { Feature, TicketGroupMetrics } from 'src/WebApiController';

import {
  EventItemHeader,
  EventItemHeaderType,
  METRICS_PLACEHOLDER_DIMS,
} from '../../Events/EventItemHeader';
import {
  ItemsCountDiv,
  TotalCostDiv,
} from '../PurchaseDatesAccordion/PurchaseDateAccordion.styled';
import { FullPageEventAction } from '../SalesEventsAccordion/FullPageEventAction';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { EventItemHeaderV2 } from 'src/components/Events/EventItemHeader/EventItemHeaderV2';

export const EventTicketGroupHeader = (props: EventItemHeaderType) => {
  const { event, ...rest } = props;
  const { performer, venue } = useGetEventFullInfo(event);
  const { eventMetrics } = useCatalogMetricsContext<TicketGroupMetrics>();
  const hasConsignorMasterUI = useUserHasConsignorMasterUI();
  const hasPurchaseTableV2Feature = useUserHasFeature(Feature.PurchaseTableV2);
  const metrics = useMemo(() => {
    return eventMetrics?.[event.virtId];
  }, [event.virtId, eventMetrics]);

  const totalCostText = useContent(ContentId.TotalCost);

  const {
    eventsExpansion: { expandedListItems, toggleListItemExpansion },
  } = useCatalogDataContext();

  const onToggleGroup = (groupId: string) => {
    if (!expandedListItems.includes(groupId)) {
      toggleListItemExpansion([groupId]);
    }
  };

  const metricsDisplay = formatCurrency(metrics?.ttlCst, metrics?.currency);

  return hasPurchaseTableV2Feature ? (
    <EventItemHeaderV2
     showYear={undefined}
      {...rest}
      event={event}
      performer={performer}
      venue={venue}
      actions={
        <>
          <FullPageEventAction key="FullEventPageToggle" event={event} />
          <MultiSelectionToggleGroup
            key="TicketGroupEventSelectionGroupToggle"
            groupId={props.event.virtId}
            onToggle={onToggleGroup}
          />
          <PurchaseTileActionDropdown event={event} />
        </>
      }
      headerMetrics={
        hasConsignorMasterUI ? null : metrics ? (
          <TotalCostDiv
            title={`${totalCostText}: ${metricsDisplay}`}
            key="total-cost"
          >
            {metricsDisplay}
          </TotalCostDiv>
        ) : (
          <MetricsDisplayPlaceholder
            key="metrics-placeholder"
            {...METRICS_PLACEHOLDER_DIMS}
          />
        )
      }
      metrics={
        hasConsignorMasterUI ? null : metrics ? (
          <ItemsCountDiv key="items-count">
            <div>
              {metrics.tktQty}{' '}
              <Content
                id={metrics.tktQty === 1 ? ContentId.Ticket : ContentId.Tickets}
              />
            </div>
          </ItemsCountDiv>
        ) : (
          <MetricsDisplayPlaceholder
            key="metrics-placeholder"
            {...METRICS_PLACEHOLDER_DIMS}
          />
        )
      }
      eventTags={metrics?.tags}
    />
  ) : (
    <EventItemHeader
      showYear={undefined}
      {...rest}
      event={event}
      performer={performer}
      venue={venue}
      actions={
        <>
          <FullPageEventAction key="FullEventPageToggle" event={event} />
          <MultiSelectionToggleGroup
            key="TicketGroupEventSelectionGroupToggle"
            groupId={props.event.virtId}
            onToggle={onToggleGroup}
          />
          <PurchaseTileActionDropdown event={event} />
        </>
      }
      headerMetrics={
        hasConsignorMasterUI ? null : metrics ? (
          <TotalCostDiv
            title={`${totalCostText}: ${metricsDisplay}`}
            key="total-cost"
          >
            {metricsDisplay}
          </TotalCostDiv>
        ) : (
          <MetricsDisplayPlaceholder
            key="metrics-placeholder"
            {...METRICS_PLACEHOLDER_DIMS}
          />
        )
      }
      metrics={
        hasConsignorMasterUI ? null : metrics ? (
          <ItemsCountDiv key="items-count">
            <div>
              {metrics.tktQty}{' '}
              <Content
                id={metrics.tktQty === 1 ? ContentId.Ticket : ContentId.Tickets}
              />
            </div>
          </ItemsCountDiv>
        ) : (
          <MetricsDisplayPlaceholder
            key="metrics-placeholder"
            {...METRICS_PLACEHOLDER_DIMS}
          />
        )
      }
      eventTags={metrics?.tags}
    />
  );
};
